// 영수증 하단 액션 버튼 컴포넌트
import { useCallback } from 'react';
import { Button } from '../ui/button';
import type { OrderData } from './types';
import { receiptTranslations } from './constants';
import { useApp } from '@/contexts/AppContext';
import { formatCurrency } from '@/utils/currency';
import { narrowReceiptView } from '@/utils/receipt/narrowReceiptView';
import { logger } from '@/utils/logger';
import { DEFAULT_STORE_TIMEZONE } from '@/utils/menuAvailability';
import { loadReceiptPdfTools } from '@/utils/pdf';

interface ReceiptActionsProps {
  orderData: OrderData;
  onBack: () => void;
}

export function ReceiptActions({
  orderData,
  onBack,
}: ReceiptActionsProps) {
  // #522: 15 locale 전수 lookup — Locale 단일 출처 정합.
  const { locale, currency } = useApp();
  const t = receiptTranslations[locale];

  // 가격 포맷팅 함수 (중앙 유틸리티 래핑)
  const formatPrice = useCallback(
    (amount: number) => formatCurrency(amount, currency),
    [currency]
  );

  const handlePDFDownload = useCallback(async () => {
    try {
      const { jsPDF, html2canvas } = await loadReceiptPdfTools();

      // #336 C: `??` / `||` fallback 을 narrowReceiptView 1곳에 집중.
      // #284: PDF/이미지 영수증도 NET 소계 사용 (narrowReceiptView 가 GROSS-tax 역산).
      const view = narrowReceiptView(orderData);
      const items = view.items;
      const tax = view.tax;
      const subtotal = view.netSubtotal;
      const tip = view.tip;
      const pointsUsed = view.pointsUsed;
      const total = view.total;

      // #296: 주문 시각을 orderData.timestamp 에서 읽어야 함. 이전 코드는 항상
      // new Date() 를 사용해 과거 주문 PDF에도 "오늘 날짜"가 찍히던 pre-existing
      // 버그. 없거나 Invalid Date 이면 '-' 플레이스홀더(인접 orderId/tableNumber
      // '|| "-"' 컨벤션과 일치) + 로그(silent failure 회피). locale은 영수증 다른
      // 화면(OrderInfo.tsx)과 일관성을 위해 'es-ES' 고정.
      const rawTs = view.timestamp;
      const validTs =
        rawTs instanceof Date && !Number.isNaN(rawTs.getTime()) ? rawTs : null;
      if (!validTs) {
        logger.warn('[ReceiptActions] PDF timestamp 누락/유효하지 않음', {
          orderId: orderData.orderId,
          rawTs,
        });
      }
      // #1816: 영수증 PDF 시각도 매장 타임존(멕시코)으로 고정 — 고객 기기 TZ 의존 차단.
      const orderDate = validTs ? validTs.toLocaleDateString('es-ES', { timeZone: DEFAULT_STORE_TIMEZONE }) : '-';
      const orderTime = validTs ? validTs.toLocaleTimeString('es-ES', { timeZone: DEFAULT_STORE_TIMEZONE }) : '-';

      // 임시 HTML 요소 생성 (화면에 보이지 않음)
      const container = document.createElement('div');
      container.style.cssText = `
        position: absolute;
        left: -9999px;
        top: 0;
        width: 400px;
        padding: 24px;
        background: white;
        font-family: 'Noto Sans', 'Noto Sans KR', sans-serif;
        color: #000;
      `;

      container.innerHTML = `
        <div style="text-align: center; margin-bottom: 16px;">
          <h1 style="font-size: 24px; font-weight: bold; margin: 0; color: #03D67B;">OLULO</h1>
          <p style="font-size: 14px; margin: 8px 0 0 0; color: #666;">${t.title}</p>
        </div>

        <hr style="border: none; border-top: 1px solid #ddd; margin: 16px 0;" />

        <div style="font-size: 12px; margin-bottom: 16px;">
          <p style="margin: 4px 0;"><strong>${t.orderNumber}:</strong> ${view.orderId || '-'}</p>
          <p style="margin: 4px 0;"><strong>${t.date}:</strong> ${orderDate}</p>
          <p style="margin: 4px 0;"><strong>${t.time}:</strong> ${orderTime}</p>
          <p style="margin: 4px 0;"><strong>${t.table || 'Table'}:</strong> ${view.tableNumber ?? '-'}</p>
        </div>

        <hr style="border: none; border-top: 1px solid #ddd; margin: 16px 0;" />

        <table style="width: 100%; font-size: 12px; border-collapse: collapse;">
          <thead>
            <tr style="border-bottom: 1px solid #ddd;">
              <th style="text-align: left; padding: 8px 0; font-weight: 600;">${t.menu}</th>
              <th style="text-align: center; padding: 8px 0; width: 50px; font-weight: 600;">${t.qty}</th>
              <th style="text-align: right; padding: 8px 0; width: 80px; font-weight: 600;">${t.price}</th>
            </tr>
          </thead>
          <tbody>
            ${items
              .map(
                (item) => `
              <tr>
                <td style="padding: 6px 0;">${item.name}</td>
                <td style="text-align: center; padding: 6px 0;">${item.quantity}</td>
                <td style="text-align: right; padding: 6px 0;">${formatPrice(item.price)}</td>
              </tr>
            `
              )
              .join('')}
          </tbody>
        </table>

        <hr style="border: none; border-top: 1px solid #ddd; margin: 16px 0;" />

        <div style="font-size: 12px;">
          <div style="display: flex; justify-content: space-between; margin: 4px 0;">
            <span>${t.subtotal}:</span>
            <span>${formatPrice(subtotal)}</span>
          </div>
          <div style="display: flex; justify-content: space-between; margin: 4px 0;">
            <span>${t.tax}:</span>
            <span>${formatPrice(tax)}</span>
          </div>
          <div style="display: flex; justify-content: space-between; margin: 4px 0;">
            <span>${t.tip}:</span>
            <span>${formatPrice(tip)}</span>
          </div>
          ${
            pointsUsed > 0
              ? `
          <div style="display: flex; justify-content: space-between; margin: 4px 0; color: #03D67B;">
            <span>${t.pointsUsed}:</span>
            <span>-${formatPrice(pointsUsed)}</span>
          </div>
          `
              : ''
          }
        </div>

        <hr style="border: none; border-top: 2px solid #000; margin: 16px 0;" />

        <div style="display: flex; justify-content: space-between; font-size: 16px; font-weight: bold;">
          <span>${t.total}:</span>
          <span>${formatPrice(total)}</span>
        </div>

        <div style="text-align: center; margin-top: 24px; font-size: 11px; color: #666;">
          <p style="margin: 4px 0;">${t.thankYou}</p>
          <p style="margin: 4px 0;">OLULO - ${t.tableOrderService}</p>
        </div>
      `;

      document.body.appendChild(container);

      // HTML을 캔버스로 변환
      const canvas = await html2canvas(container, {
        scale: 2, // 고해상도
        useCORS: true,
        backgroundColor: '#ffffff',
      });

      // 임시 요소 제거
      document.body.removeChild(container);

      // PDF 생성
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4',
      });

      // 이미지 크기 계산 (A4 너비에 맞춤)
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (canvas.height * pdfWidth) / canvas.width;

      pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);

      // PDF 다운로드
      // #296: 파일명 식별자는 orderId 우선(있으면 변조 없는 고유 식별자), 없으면
      // 유효한 timestamp 의 ISO 일자. 이전 구현은 new Date() 를 fallback 으로 써서
      // 원 버그(과거 주문 PDF 가 오늘 날짜로 저장됨)를 파일명 레벨에서 재현했음.
      // #1816: 파일명 일자도 매장 타임존 기준(en-CA = YYYY-MM-DD) — toISOString()=UTC 라
      // 자정 부근 표시 영수증 날짜와 파일명이 어긋나는 것을 방지.
      const isoDate = validTs
        ? validTs.toLocaleDateString('en-CA', { timeZone: DEFAULT_STORE_TIMEZONE })
        : undefined;
      const fileNameSegment = orderData.orderId ?? isoDate ?? 'unknown';
      const fileName = `OLULO_Receipt_${fileNameSegment}.pdf`;
      pdf.save(fileName);

      logger.info('PDF 다운로드 완료:', fileName);
    } catch (error) {
      logger.error('PDF 생성 중 오류:', error);
      // #522: 에러 alert 도 locale 분기 — ko/es 외 신규 locale 은 en 폴백 유지.
      alert(
        locale === 'ko'
          ? 'PDF 생성 중 오류가 발생했습니다.'
          : locale === 'es'
            ? 'Error al generar PDF.'
            : 'Error generating PDF.'
      );
    }
  }, [orderData, locale, t, formatPrice]);

  return (
    <div className="fixed bottom-0 left-0 right-0 bg-background border-t border-border z-50">
      <div className="w-full max-w-2xl mx-auto p-4 space-y-2">
        {/* PDF 다운로드 버튼 */}
        <div className="flex flex-row gap-2">
          <button
            onClick={handlePDFDownload}
            className="flex flex-row items-center justify-center gap-1.5 flex-1 h-[36px] px-3 bg-background border-2 border-primary hover:bg-primary/5 rounded-[5px] transition-all duration-200 active:scale-[0.98]"
          >
            <svg
              className="w-3.5 h-3.5 text-primary shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2.5}
                d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
            <span className="font-bold text-sm text-primary whitespace-nowrap">{t.receipt}</span>
          </button>
        </div>

        {/* 닫기 버튼 */}
        <div>
          <Button
            onClick={onBack}
            className="w-full bg-primary hover:bg-primary-hover text-white h-[50px] font-bold rounded-[5px] transition-colors duration-200"
          >
            {t.close}
          </Button>
        </div>
      </div>
    </div>
  );
}
